import { useRef, useState } from "react";
import { api, API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Download, Trash2, Loader2, Plus, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";

const DOCS_BASE = "https://www.angenapoli.com/uploads/1/1/7/4/117407509/";
export const DEFAULT_MODULI = [
  ["Domanda di ammissione soci", "domanda_ammissione_soci.pdf"],
  ["Domanda di ammissione al corso di formazione", "domanda_ammissione_corso_di_formazione.pdf"],
  ["Dichiarazione sostitutiva di certificazioni", "dichiarazione_sostitutiva_di_certificazioni_per_iscrizione.pdf"],
  ["Informativa trattamento dati personali", "trattamento_dati_personali_napoli_2018.pdf"],
  ["Estratto dello Statuto", "statuto_2019_estratto.pdf"],
].map(([titolo, f]) => ({ id: f, titolo, href: DOCS_BASE + f }));

const LinkCard = ({ m, i }) => (
  <a href={m.href} target="_blank" rel="noopener noreferrer" data-testid={`sito-modulo-${i}`} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-5 py-4 transition-colors hover:border-[#166534] hover:bg-emerald-50">
    <span className="flex items-center gap-3 text-sm font-medium text-slate-800"><FileText className="h-5 w-5 text-[#166534]" /> {m.titolo}</span>
    <Download className="h-4 w-4 text-slate-400" />
  </a>
);

const postFile = (url, method, titolo, file) => {
  const fd = new FormData();
  if (titolo !== undefined) { fd.append("titolo", titolo); }
  if (file) { fd.append("file", file); }
  return api.request({ url, method, data: fd });
};

const EditRow = ({ m, i, refresh }) => {
  const [titolo, setTitolo] = useState(m.titolo);
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); refresh(); } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Operazione non riuscita"); } finally { setBusy(false); }
  };
  const replace = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { run(() => postFile(`/sito/moduli/${m.id}`, "put", undefined, f), "PDF sostituito"); } };
  const remove = () => { if (window.confirm(`Eliminare "${m.titolo}"?`)) { run(() => api.delete(`/sito/moduli/${m.id}`), "Modulo eliminato"); } };
  return (
    <div data-testid={`modulo-edit-${i}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-white p-3">
      <FileText className="h-5 w-5 text-[#166534]" />
      <Input value={titolo} onChange={(e) => setTitolo(e.target.value)} className="min-w-[180px] flex-1" data-testid={`modulo-title-${i}`} />
      {titolo !== m.titolo && <Button size="sm" onClick={() => run(() => postFile(`/sito/moduli/${m.id}`, "put", titolo), "Titolo salvato")} disabled={busy} data-testid={`modulo-save-${i}`}><Save className="h-4 w-4" /></Button>}
      <a href={`${API}/public/sito/moduli/${m.id}/file`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#166534] hover:underline" data-testid={`modulo-open-${i}`}>Apri</a>
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-slate-300 px-2 py-1.5 text-xs hover:bg-slate-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sostituisci
        <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={replace} data-testid={`modulo-replace-${i}`} />
      </label>
      <Button size="icon" variant="outline" className="h-8 w-8 text-red-700" onClick={remove} data-testid={`modulo-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
};

const AddModulo = ({ refresh }) => {
  const [titolo, setTitolo] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const add = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) { return; }
    setBusy(true);
    try { await postFile("/sito/moduli", "post", titolo || f.name.replace(/\.pdf$/i, ""), f); toast.success("Modulo aggiunto"); setTitolo(""); refresh(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Caricamento non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-amber-400 bg-amber-50 p-3" data-testid="modulo-add">
      <Input value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder="Titolo del nuovo modulo" className="min-w-[200px] flex-1 bg-white" data-testid="modulo-add-title" />
      <input ref={ref} type="file" accept="application/pdf,.pdf" className="hidden" onChange={add} data-testid="modulo-add-input" />
      <Button size="sm" onClick={() => ref.current?.click()} disabled={busy} className="bg-[#14382C] hover:bg-[#0E281F]" data-testid="modulo-add-button">
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} Carica PDF
      </Button>
    </div>
  );
};

export const ModuliList = ({ moduli, edit, refresh }) => {
  const usingDefaults = moduli.length === 0;
  const shown = usingDefaults ? DEFAULT_MODULI : moduli.map((m) => ({ ...m, href: `${API}/public/sito/moduli/${m.id}/file` }));
  return (
    <div className="space-y-3">
      {edit && usingDefaults && <p className="text-xs text-amber-700" data-testid="moduli-defaults-note">Ora sono mostrati i moduli del vecchio sito angenapoli.com. Appena carichi il primo PDF, verranno mostrati solo i moduli caricati qui.</p>}
      {edit && !usingDefaults ? (
        <div className="space-y-2">{moduli.map((m, i) => <EditRow key={m.id} m={m} i={i} refresh={refresh} />)}</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">{shown.map((m, i) => <LinkCard key={m.id} m={m} i={i} />)}</div>
      )}
      {edit && <AddModulo refresh={refresh} />}
    </div>
  );
};

export const fetchModuli = () => api.get("/public/sito/moduli").then((r) => r.data || []);
